<template>
  <div id="app">
    <div id="nav" :key="navKey">
      <router-view v-if="$route.meta.isLogin"></router-view>
      <admin-home v-if="$route.meta.isAdmin" />
      <Home v-if="Showhome" />
    </div>

    <!-- ⭐ 添加 class="notranslate" 和 translate="no" 双重保护 -->
    <div
      ref="languageSwitcher"
      class="language-switcher notranslate"
      v-if="showLanguageSwitcher"
      :class="{ 'language-switcher-mobile': windowWidth < 850 }"
      translate="no"
      data-translate="no"
    >
      <template v-for="(text, index) in buttonTexts">
        <button
          :key="'btn-' + index"
          class="lang-btn notranslate"
          @click="switchLanguage(langCodes[index])"
          :class="{ active: currentLang === langCodes[index] }"
          :data-original="text"
          translate="no"
          data-translate="no"
        >
          {{ text }}
        </button>
        <span
          v-if="index < buttonTexts.length - 1"
          :key="'div-' + index"
          class="divider notranslate"
          translate="no"
          data-translate="no"
        >|</span>
      </template>
    </div>
  </div>
</template>

<script>
import Home from "@/views/Home.vue";
import AdminHome from "@/views/Home1.vue";

export default {
  name: "App",
  components: { Home, AdminHome },
  data() {
    return {
      Showhome: false,
      currentLang: sessionStorage.getItem("preferred_language") || "en",
      translateReady: false,
      navKey: 0,
      windowWidth: window.innerWidth,
      buttonTexts: ['EN', '中文', 'ਪੰਜਾਬੀ', 'हिन्दी'],
      langCodes: ['en', 'zh-CN', 'pa', 'hi'],
      observer: null,
      restoreTimer1: null,
      restoreTimer2: null,
      isTranslating: false, // ⭐ 添加翻译状态标志
    };
  },
  computed: {
    showLanguageSwitcher() {
      return !(this.$route.meta.isAdmin || this.$route.meta.isLogin);
    },
  },
  watch: {
    $route() {
      this.Showhome = !(this.$route.meta.isAdmin || this.$route.meta.isLogin);
      if (this.translateReady && this.currentLang !== "en") {
        this.$nextTick(() => {
          this.executeTranslateWithProtection();
        });
      }
    },
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // ⭐ 执行翻译并保护按钮文字（优化版）
    executeTranslateWithProtection() {
      // 清除之前的定时器
      this.clearRestoreTimers();
      
      // 设置翻译状态
      this.isTranslating = true;
      
      // 先保存原始文字
      this.saveButtonTexts();
      
      // 执行翻译
      try {
        window.translate.execute();
      } catch (e) {
        console.warn('翻译执行失败:', e);
      }
      
      // 延迟恢复（等待翻译完成）
      this.restoreTimer1 = setTimeout(() => {
        this.restoreButtonTexts();
        this.isTranslating = false;
      }, 100);
      
      // 二次恢复（确保安全）
      this.restoreTimer2 = setTimeout(() => {
        this.restoreButtonTexts();
        this.isTranslating = false;
      }, 300);
    },

    // ⭐ 清理定时器
    clearRestoreTimers() {
      if (this.restoreTimer1) {
        clearTimeout(this.restoreTimer1);
        this.restoreTimer1 = null;
      }
      if (this.restoreTimer2) {
        clearTimeout(this.restoreTimer2);
        this.restoreTimer2 = null;
      }
    },

    // ⭐ 保存按钮原始文字
    saveButtonTexts() {
      const container = this.$refs.languageSwitcher;
      if (!container) return;
      
      const buttons = container.querySelectorAll('.lang-btn');
      buttons.forEach((btn, index) => {
        if (index < this.buttonTexts.length) {
          btn.setAttribute('data-original', this.buttonTexts[index]);
        }
      });
    },

    // ⭐ 恢复按钮文字
    restoreButtonTexts() {
      const container = this.$refs.languageSwitcher;
      if (!container) return;
      
      const buttons = container.querySelectorAll('.lang-btn');
      let hasChanged = false;
      
      buttons.forEach((btn) => {
        const original = btn.getAttribute('data-original');
        if (original && btn.textContent.trim() !== original) {
          btn.textContent = original;
          hasChanged = true;
        }
      });
      
      return hasChanged;
    },

    // ⭐ 设置 MutationObserver
    setupMutationObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      
      const container = this.$refs.languageSwitcher;
      if (!container) return;
      
      this.observer = new MutationObserver((mutations) => {
        let needRestore = false;
        
        for (const mutation of mutations) {
          if (mutation.type === 'characterData') {
            const parent = mutation.target.parentElement;
            if (parent?.classList?.contains('lang-btn')) {
              needRestore = true;
              break;
            }
          }
          
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.classList?.contains('lang-btn')) {
                needRestore = true;
              }
            });
          }
        }
        
        if (needRestore) {
          requestAnimationFrame(() => {
            const changed = this.restoreButtonTexts();
            if (changed) {
              setTimeout(() => {
                this.restoreButtonTexts();
              }, 50);
            }
          });
        }
      });
      
      this.observer.observe(container, {
        characterData: true,
        childList: true,
        subtree: true,
        characterDataOldValue: true,
      });
    },

    initTranslate() {
      if (!window.translate) {
        if (!this._retryCount) this._retryCount = 0;
        if (this._retryCount < 10) {
          this._retryCount++;
          setTimeout(() => this.initTranslate(), 300);
        } else {
          console.error("translate.js 加载超时");
        }
        return;
      }

      window.translate.language.setLocal("english");

      const langMap = {
        en: "english",
        "zh-CN": "chinese_simplified",
        pa: "punjabi",
        hi: "hindi",
      };
      const targetLang = langMap[this.currentLang] || "english";
      window.translate.to = targetLang;
      window.translate.storage.set("to", targetLang);

      window.translate.service.use("client.edge");
      window.translate.listener.start();

      this.translateReady = true;
      this._retryCount = 0;

      this.$nextTick(() => {
        this.saveButtonTexts();
        this.setupMutationObserver();
        
        if (this.currentLang !== "en") {
          setTimeout(() => {
            this.executeTranslateWithProtection();
          }, 150);
        }
        
        console.log("✅ 翻译初始化完成，目标语言:", targetLang);
      });
    },

    // ⭐ 优化切换语言 - 减少闪烁
    switchLanguage(lang) {
      if (this.currentLang === lang || !this.translateReady || this.isTranslating) return;

      // 清除缓存
      if (window.translate.cache) {
        window.translate.cache = {};
      }

      // 更新语言
      this.currentLang = lang;
      sessionStorage.setItem("preferred_language", lang);

      const langMap = {
        en: "english",
        "zh-CN": "chinese_simplified",
        pa: "punjabi",
        hi: "hindi",
      };
      
      const targetLang = langMap[lang] || "english";
      
      try {
        // 设置目标语言
        window.translate.language.setLocal("english");
        window.translate.to = targetLang;
        window.translate.storage.set("to", targetLang);
        
        // ⭐ 关键优化：在重新渲染前先保存文字
        this.saveButtonTexts();
        
        // 强制重新渲染
        this.navKey++;
        
        this.$nextTick(() => {
          // ⭐ 在 DOM 更新后立即恢复文字（在翻译执行之前）
          this.restoreButtonTexts();
          
          requestAnimationFrame(() => {
            // 再次确保语言设置
            window.translate.language.setLocal("english");
            window.translate.to = targetLang;
            window.translate.storage.set("to", targetLang);
            
            // 执行翻译
            this.executeTranslateWithProtection();
            
            console.log(`✅ 成功切换到: ${targetLang}`);
          });
        });
      } catch (e) {
        console.warn(`切换到 ${lang} 失败:`, e);
      }
    }
  },
  mounted() {
    this.initTranslate();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.clearRestoreTimers();
  },
};
</script>

<style lang="scss">
#app {
  font-family: Cotyledon-Regular, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: #fff;
  display: flex;
  justify-content: space-around;
}

#nav {
  width: 100%;
  min-height: 100vh;
  background-color: #f8fafc;
}

.language-switcher {
  position: fixed;
  top: 20px;
  right: 30px;
  z-index: 9999;
  background: #ffffff;
  padding: 6px 14px;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  border: 1px solid #e8ecf1;
  flex-wrap: wrap;
}

.language-switcher button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #8898aa;
  padding: 4px 8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
}

.language-switcher button:hover {
  color: #2c3e50;
  background: #f0f2f5;
}

.language-switcher button.active {
  color: #007bff;
  background: #e8f0fe;
}

.language-switcher .divider {
  color: #dce1e8;
  font-size: 14px;
}

/* ⭐ 关键：阻止 translate.js 翻译 */
.notranslate {
  translate: none !important;
  -webkit-translate: none !important;
}

/* ⭐ 使用 data 属性阻止翻译 */
[translate="no"],
[data-translate="no"] {
  translate: none !important;
  -webkit-translate: none !important;
}

/* ⭐ 隐藏 translate.js 自带的翻译工具条 */
#translate {
  display: none !important;
}

/* ⭐ 优化：添加过渡效果减少闪烁感 */
.language-switcher button {
  transition: color 0.2s ease, background 0.2s ease;
}

.language-switcher-mobile {
  top: 80px !important;
  right: 20px !important;
  padding: 4px 12px !important;
  font-size: 12px !important;
}

@media (max-width: 480px) {
  .language-switcher-mobile {
    top: 10px !important;
    right: 80px !important;
    padding: 6px 10px !important;
    font-size: 11px !important;
  }
  .language-switcher-mobile button {
    font-size: 10px !important;
    padding: 2px 5px !important;
  }
  //   .language-switcher-mobile {
  //   top: 10px !important;
  //   right: 70px !important;
  //   padding: 6px 12px !important;
  //   font-size: 13px !important;
  //   gap: 5px !important;
  //   border-radius: 18px !important;
  // }
  
  // .language-switcher-mobile button {
  //   font-size: 12px !important;
  //   padding: 5px 10px !important;
  //   min-width: 34px !important;
  //   min-height: 34px !important;
  //   border-radius: 10px !important;
  // }
  
  // .language-switcher-mobile .divider {
  //   font-size: 11px !important;
  //   padding: 0 2px !important;
  // }
  //   top: 10px !important;
  //   right: 80px !important;
  //   padding: 4px 10px !important;
  //   font-size: 11px !important;
  // }
  // .language-switcher-mobile button {
  //   font-size: 10px !important;
  //   padding: 2px 5px !important;
  // }
}
</style>